import * as ko from 'knockout';
import page from 'page';

import { DimensionMeta } from '../models/dimension_meta';
import { CropVariety } from '../models/crop_variety';
import { applyCodeFormatValidation } from '../ko_bindings/slug_validation';
import { CROP_VARIETY_SLUG } from '../api/dimension_metas';
import * as cropVarietiesApi from '../api/crop_varieties';
import { BaseDimensionRecordEditDelegate } from '../components/base_dimension_record_edit';
import { Deferred } from '../utils/deferred';
import { getCropSearchConfig } from '../components/configs/search_configs';
import { confirmDialog } from '../components/confirm_dialog';
import { asI18nText, translate } from '../i18n_text';
import { CropData } from '../api/crops';
import { KOMaybeArray } from '../utils/ko_utils';
import { asArray } from '../utils';
import i18n from '../i18n';
import { canEditCropVariety } from '../permissions';
import { session } from '../session';
import { app } from '../app';
import { extractServerErrorMessages } from '../api/request';

let template = require('raw-loader!../../templates/crop_variety_edit.html').default;

class CropVarietyEditScreen
  implements BaseDimensionRecordEditDelegate<cropVarietiesApi.CropVarietyData, CropVariety>
{
  disableApplications = true;
  allowAnonymize = true;
  allowLinkingCrops = false; // Only one crop for Crop Variety
  anyoneCanEdit = false;
  editPermissionCheck = () => canEditCropVariety(session.tenant());
  canEdit = ko.observable(canEditCropVariety(session.tenant()));
  anonymizedCodeText = APP_CONFIG.AUTO_CV_CODES
    ? i18n.t('Code (leave empty for automatic code)')()
    : undefined;

  dimensionMetaId = CROP_VARIETY_SLUG;
  dimensionId: string;
  result: Deferred<cropVarietiesApi.CropVarietyData>;
  private initialName: string;
  private initialCrop: KOMaybeArray<CropData>;
  private selectCreatedEntity: (entity: cropVarietiesApi.CropVarietyData) => void;

  dimension = ko.observable<CropVariety>(null);
  isProcessing = ko.observable(false);

  cropSearchConfig = ko.pureComputed(() => {
    return getCropSearchConfig(this.dimension() ? this.dimension().crop : undefined);
  });

  addAnother: (data: cropVarietiesApi.CropVarietyData, dm: DimensionMeta) => void = undefined;

  constructor(params: {
    id: string;
    initialName?: string;
    initialCrop?: KOMaybeArray<CropData>;
    multi?: boolean;
    result?: Deferred<cropVarietiesApi.CropVarietyData>;
    selectCreatedEntity: (entity: cropVarietiesApi.CropVarietyData) => void;
  }) {
    this.dimensionId = params.id;
    this.result = params.result;
    this.initialName = params.initialName;
    this.initialCrop = params.initialCrop;
    this.selectCreatedEntity = params.selectCreatedEntity;

    if (params.multi) {
      this.addAnother = (data, dm) => {
        if (this.selectCreatedEntity) {
          this.selectCreatedEntity(data);
        }

        let cv = this.instantiate(undefined, dm);
        // Built here rather than through the component's load path, so the rule has to be applied
        // again or every variety after the first loses the inline message.
        applyCodeFormatValidation(cv.code);
        cv.crop(this.dimension().crop());
        this.dimension(cv);
      };
    }
  }

  fetch(dimensionMetaId: string, id: string) {
    return cropVarietiesApi.retrieve(id);
  }

  instantiate(data: cropVarietiesApi.CropVarietyData, dimensionMeta: DimensionMeta) {
    let cv = new CropVariety(data, dimensionMeta);
    if (!data) {
      if (this.initialName) {
        cv.nameJson(asI18nText(this.initialName));
      }
      if (this.initialCrop) {
        let crops = asArray(this.initialCrop());
        if (crops.length === 1) {
          cv.crop(crops[0]);
        }
      }
    }

    return cv;
  }

  saveRequest(data: cropVarietiesApi.CropVarietyData) {
    return cropVarietiesApi.save(data);
  }

  replaceVariety = () => {
    const dimension = this.dimension();
    const selected = dimension.selectedOther();
    const keep = dimension.isCanonical() ? 'current' : dimension.replaceMode();
    if (!selected || !keep) {
      return;
    }

    const currentId = dimension.id();
    const [canonicalId, duplicateId] =
      keep === 'selected' ? [selected.id, currentId] : [currentId, selected.id];

    this.runProcessing(() =>
      this.ensureDuplicateArchivable(duplicateId).then((archivable) =>
        archivable
          ? this.performReplace(canonicalId, duplicateId, keep === 'selected', selected.id)
          : undefined
      )
    );
  };

  private runProcessing(action: () => Promise<unknown>) {
    this.isProcessing(true);
    action().then(
      () => this.isProcessing(false),
      () => this.isProcessing(false)
    );
  }

  private ensureDuplicateArchivable(duplicateId: string): Promise<boolean> {
    return cropVarietiesApi.retrieve(duplicateId).then((variety) => {
      const replacing = variety.replacing ?? [];
      if (replacing.length === 0) {
        return true;
      }
      const name = translate(variety.name_json);
      const names = replacing.map((duplicate) => duplicate.name).join(', ');
      return confirmDialog(i18n.t('"{{ name }}" already replaces other varieties', { name })(), [
        i18n.t('It currently replaces: {{ names }}.', { names })(),
        i18n.t('Archiving it will undo those replacements first. Continue?')(),
      ]).then(
        () => cropVarietiesApi.undoAllReplacements(duplicateId).then(() => true),
        () => false
      );
    });
  }

  private performReplace(
    canonicalId: string,
    duplicateId: string,
    keepSelected: boolean,
    selectedId: string
  ): Promise<void> {
    const dimension = this.dimension();
    return cropVarietiesApi.replace(canonicalId, duplicateId).then((result) => {
      if (!result.isValid) {
        this.showReplaceFailure(result.errors);
        return;
      }
      if (keepSelected) {
        page.redirect(session.toTenantPath('/crop_varieties/' + selectedId + '/'));
        return;
      }
      dimension.replacing.push(result.originalResponse);
      dimension.selectedOther(null);
      dimension.replaceMode(null);
    });
  }

  private showReplaceFailure(serverErrors: unknown) {
    const [message] = extractServerErrorMessages(serverErrors);
    app.showFormSubmissionErrorModal(message || i18n.t('This variety could not be replaced.')(), {
      reload: false,
    });
  }

  undoReplacement = (duplicate: cropVarietiesApi.ReplacedVarietyData) => {
    const dimension = this.dimension();
    this.runProcessing(() =>
      cropVarietiesApi.undoReplacement(dimension.id(), duplicate.id).then(() => {
        dimension.replacing.remove(duplicate);
      })
    );
  };

  undoAllReplacements = () => {
    const dimension = this.dimension();
    confirmDialog(i18n.t('Undo all replacements?')(), [
      i18n.t(
        'This will restore every variety currently merged into this one. \
        You will have to recreate each replacement manually to undo this.'
      )(),
    ]).then(() => {
      this.runProcessing(() =>
        cropVarietiesApi.undoAllReplacements(dimension.id()).then(() => {
          dimension.replacing([]);
        })
      );
    });
  };

  undoReplacedBy = () => {
    const dimension = this.dimension();
    const canonical = dimension.replacedBy();
    if (!canonical) {
      return;
    }
    this.runProcessing(() =>
      cropVarietiesApi.undoReplacement(canonical.id, dimension.id()).then(() => {
        dimension.replacedBy(null);
      })
    );
  };

  openCanonical = () => {
    const canonical = this.dimension().replacedBy();
    if (canonical) {
      page(session.toTenantPath('/crop_varieties/' + canonical.id + '/'));
    }
  };
}

export let cropVarietyEdit = {
  name: 'crop-variety-edit',
  viewModel: CropVarietyEditScreen,
  template: template,
};

ko.components.register(cropVarietyEdit.name, cropVarietyEdit);
